import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import '../../accounting/accounting.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm, useReasonGate } from '../../../components/confirm';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { api, runWithAuditReason } from '../../../api/client';
import { useDepartments, useSaveDepartment, useDeleteDepartment, errMessage, type Department } from './systemMasters';
import { DepartmentEmployeesPanel } from './DepartmentEmployeesPanel';

interface Props { onHome?: () => void; }

/** Settings ▸ System Management ▸ Department — CRUD over dbo.Department (Name + Sort). */
export function DepartmentPage({ onHome }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm, alert } = useConfirm();
  const gateReason = useReasonGate();
  const { data: rows = [], isFetching, refetch } = useDepartments();
  const loadMs = useFetchMs(isFetching);
  const save = useSaveDepartment();
  const del = useDeleteDepartment();

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [editing, setEditing] = useState<Department | null | undefined>(undefined);
  const [name, setName] = useState('');
  const [sort, setSort] = useState('');
  const [parentId, setParentId] = useState('');
  const [err, setErr] = useState('');
  const [stagedEmpIds, setStagedEmpIds] = useState<Set<string>>(new Set());        // employees linked to a NEW (unsaved) department, committed on save
  // Inline row-edit (1st pencil) — Department / Parent / Sort all editable in place (LIMS parity).
  const [inlineId, setInlineId] = useState<string | null>(null);
  const [dName, setDName] = useState('');
  const [dParent, setDParent] = useState('');
  const [dSort, setDSort] = useState('');
  const [savingInline, setSavingInline] = useState(false);
  // Child forms open maximized by default (LIMS parity), filling the content area
  // (the grid card region) — restore returns to the normal centred dialog.
  const [maxed, setMaxed] = useState(false);  // open RIGHT-SIZED (not full-screen); user can maximize via the header button
  const cardRef = useRef<HTMLDivElement>(null);
  const [maxRect, setMaxRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    if (!maxed) { setMaxRect(null); return; }
    const measure = () => {
      const r = cardRef.current?.getBoundingClientRect();
      if (r) setMaxRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [maxed]);
  const maxStyle = maxed && maxRect
    ? { position: 'fixed' as const, top: maxRect.top, left: maxRect.left, width: maxRect.width,
        height: maxRect.height, maxWidth: 'none' as const, maxHeight: 'none' as const, margin: 0 }
    : undefined;

  const openNew = () => {
    const nextSort = rows.reduce((m, r) => Math.max(m, r.sort ?? 0), 0) + 1;   // next Sort = MAX+1 (LIMS pre-fills it)
    setEditing(null); setName(''); setSort(String(nextSort)); setParentId(''); setErr(''); setMaxed(false); setStagedEmpIds(new Set());
  };
  const openEdit = (r: Department) => { setEditing(r); setName(r.name ?? ''); setSort(r.sort != null ? String(r.sort) : ''); setParentId(r.parentId ?? ''); setErr(''); setMaxed(false); setStagedEmpIds(new Set()); };
  const editSelected = () => {
    if (selectedIds.size !== 1) { notify.warning('Select exactly one department to edit.'); return; }
    const r = rows.find((x) => x.id === String([...selectedIds][0])); if (r) openEdit(r);
  };
  const close = () => setEditing(undefined);

  const startInline = (r: Department) => { setInlineId(r.id); setDName(r.name ?? ''); setDParent(r.parentId ?? ''); setDSort(r.sort != null ? String(r.sort) : ''); };
  const cancelInline = () => { setInlineId(null); setDName(''); setDParent(''); setDSort(''); };
  const saveInline = async (r: Department) => {
    if (!dName.trim()) { notify.warning('Department must not be empty'); return; }
    // Reason-for-change gate (standing rule 11) — only when ReasonRequired is ON.
    const reason = await gateReason('Department', 'edit');
    if (reason === null) return;
    setSavingInline(true);
    try {
      await runWithAuditReason(reason, () =>
        save.mutateAsync({ id: r.id, name: dName.trim(), sort: dSort.trim() === '' ? null : Number(dSort), parentId: dParent || null }));
      await alert({ title: 'Success', message: 'Saved successfully.', tone: 'success' });
      cancelInline();
    } catch (e) { notify.error(errMessage(e)); }
    finally { setSavingInline(false); }
  };

  // LIMS New/Edit footer offers three save variants — Save (persist, stay open),
  // Save and Close (persist + back to list), Save and New (persist + fresh form).
  const submit = async (mode: 'save' | 'close' | 'new' = 'close') => {
    if (!name.trim()) { setErr('Department must not be empty'); await alert({ title: 'Validation', message: 'Department must not be empty', tone: 'error' }); return; }
    setErr('');
    const sortNum = sort.trim() === '' ? null : Number(sort);
    let savedId: string | null = editing?.id ?? null;
    const wasNew = !editing?.id;
    // Reason-for-change gate (standing rule 11) — only on an EDIT; Create is exempt.
    let reason = '';
    if (!wasNew) {
      const r = await gateReason('Department', 'edit');
      if (r === null) return;
      reason = r;
    }
    await runSave({
      save: () => runWithAuditReason(reason, async () => {
        const newId = await save.mutateAsync({ id: editing?.id, name: name.trim(), sort: sortNum, parentId: parentId || null });
        savedId = newId;
        // New department: commit the employees staged on the Employees tab.
        if (wasNew && newId && stagedEmpIds.size > 0) {
          for (const empId of stagedEmpIds) await api.put(`/api/v1/system/departments/${newId}/employees/${empId}`, {});
        }
      }),
      successMessage: 'Saved successfully.',
      onSaved: () => {
        if (mode === 'close') { close(); return; }
        if (mode === 'new') { openNew(); return; }
        // 'save' — keep the form open; a brand-new record now becomes an edit.
        if (wasNew && savedId) {
          setStagedEmpIds(new Set());
          setEditing({ id: savedId, name: name.trim(), sort: sortNum, parentId: parentId || null, parent: rows.find((d) => d.id === parentId)?.name ?? null });
        }
      },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  const removeSelected = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one department to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} department${selectedIds.size === 1 ? '' : 's'}?` })) return;
    const reason = await gateReason('Department', 'delete');
    if (reason === null) return;
    try { await runWithAuditReason(reason, async () => { for (const id of selectedIds) await del.mutateAsync(String(id)); }); setSelectedIds(new Set()); await alert({ title: 'Success', message: 'Deleted.', tone: 'success' }); }
    catch (e) { notify.error(errMessage(e)); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => (r.name ?? '').toLowerCase().includes(q)) : rows;
  }, [rows, search]);

  const columns: AppColumn<Department>[] = [
    {
      field: 'inlineedit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) =>
        inlineId === r.id ? (
          <span className="pts-rowedit" onClick={(e) => e.stopPropagation()}>
            <button className="pts-icon-btn save" title="Save" disabled={savingInline} onClick={() => void saveInline(r)}><i className="ti ti-device-floppy" /></button>
            <button className="pts-icon-btn cancel" title="Cancel" disabled={savingInline} onClick={cancelInline}><i className="ti ti-arrow-back-up" /></button>
          </span>
        ) : (
          <button className="pts-icon-btn edit" title="Inline edit" onClick={(e) => { e.stopPropagation(); startInline(r); }}><i className="ti ti-edit" /></button>
        ),
    },
    {
      field: 'editopen', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) => inlineId === r.id ? null : (
        <button className="pts-icon-btn edit" title="Open full edit screen" onClick={(e) => { e.stopPropagation(); openEdit(r); }}><i className="ti ti-external-link" /></button>
      ),
    },
    {
      // LIMS splits Department / Parent / Sort as roughly equal thirds that fill
      // the grid width (Sort right-aligned), so match that proportion here.
      field: 'name', header: 'Department', width: '34%', minWidth: 200, text: (r) => r.name ?? '',
      compare: (a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }),
      render: (r) => inlineId === r.id ? (
        <input className="pts-input" autoFocus value={dName} disabled={savingInline} onClick={(e) => e.stopPropagation()}
          onChange={(e) => setDName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void saveInline(r); if (e.key === 'Escape') cancelInline(); }} />
      ) : (<span>{r.name ?? ''}</span>),
    },
    {
      field: 'parent', header: 'Parent', width: '33%', minWidth: 180, sortable: false, text: (r) => r.parent ?? '',
      render: (r) => inlineId === r.id ? (
        // Inline Parent = dropdown + erase (self excluded), like LIMS.
        <div className="acc-lookup" onClick={(e) => e.stopPropagation()}>
          <select className="pts-input" value={dParent} disabled={savingInline} onChange={(e) => setDParent(e.target.value)}>
            <option value="">N/A</option>
            {rows.filter((d) => d.id !== r.id).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <button className="acc-lookup-btn" title="Erase" disabled={!dParent || savingInline} onClick={() => setDParent('')}><i className="ti ti-eraser" /></button>
        </div>
      ) : (<span>{r.parent ?? ''}</span>),
    },
    {
      field: 'sort', header: 'Sort', width: '33%', minWidth: 120, align: 'right', type: 'number', sortable: false, value: (r) => r.sort ?? null, text: (r) => r.sort != null ? String(r.sort) : '',
      render: (r) => inlineId === r.id ? (
        <input className="pts-input" type="number" value={dSort} disabled={savingInline} onClick={(e) => e.stopPropagation()}
          onChange={(e) => setDSort(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void saveInline(r); if (e.key === 'Escape') cancelInline(); }} />
      ) : (<span>{r.sort != null ? String(r.sort) : ''}</span>),
    },
  ];

  return (
    <div className="acc-page">
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Department</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        <FormTitle pageId="sm-department" loadMs={loadMs ?? null} loading={isFetching} auditFormName="Department" />
        <div className="acc-sub">{rows.length} item{rows.length === 1 ? '' : 's'}</div>
      </div>
      <div className="acc-body">
        {/* Action toolbar — its OWN full-width row (BO UI Standard §6); Export far right. */}
        <AppToolbar
          onNew={openNew}
          onEdit={editSelected} editDisabled={selectedIds.size !== 1}
          onDelete={() => void removeSelected()} deleteDisabled={selectedIds.size === 0}
          onRefresh={() => void refetch()} refreshing={isFetching}
          right={
            <span className="app-toolbar__spacer">
              <ExportMenu triggerClassName="ca-btn" filename="Department" headers={['Department', 'Parent', 'Sort']} rows={visible.map((r) => [r.name ?? '', r.parent ?? '', r.sort != null ? String(r.sort) : ''])} />
            </span>
          }
        />
        {/* Search lives in its OWN row BELOW the toolbar (standard) — never inside the card. */}
        <div className="list-search-row">
          <div className="list-search"><span className="tb-i">🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
        </div>
        {/* ONE white grid card — grid + its single footer only. */}
        <div className="acc-card" ref={cardRef}>
          <AppDataGrid<Department> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds} selectedRowId={inlineId ?? editing?.id ?? null}
            initialSort={{ key: 'name', dir: 'asc' }} loading={isFetching} emptyText="No departments found." />
        </div>
      </div>

      {editing !== undefined && (
        <div className="acc-overlay" onMouseDown={close}>
          <div className={`acc-dialog acc-dialog-md${maxed ? ' acc-dialog-max' : ''}`} style={maxStyle} onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr">
              <span>{editing ? `Department - ${editing.name ?? ''}` : 'Department'}</span>
              <span className="acc-dialog-acts">
                <i className={`ti ${maxed ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} title={maxed ? 'Restore' : 'Maximize'} onClick={() => setMaxed((m) => !m)} />
                <i className="ti ti-x acc-dialog-x" onClick={close} />
              </span>
            </div>
            <div className="acc-dialog-body">
              <div className="acc-field-h">
                <label className="acc-label acc-label-req">Department<span className="req">*</span></label>
                <div className="acc-fh-control">
                  <input className="acc-input" autoFocus value={name} onChange={(e) => { setName(e.target.value); if (err) setErr(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
                  {err && <div className="acc-err">{err}</div>}
                </div>
              </div>
              <div className="acc-field-h">
                <label className="acc-label">Parent</label>
                <div className="acc-fh-control">
                  {/* Parent lookup: dropdown + erase (clear) icon — LIMS Department.Parent self-FK. */}
                  <div className="acc-lookup">
                    <select className="acc-input" value={parentId} onChange={(e) => setParentId(e.target.value)}>
                      <option value="">N/A</option>
                      {rows.filter((d) => d.id !== editing?.id).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                    <button className="acc-lookup-btn" title="Erase" disabled={!parentId} onClick={() => setParentId('')}><i className="ti ti-eraser" /></button>
                  </div>
                </div>
              </div>
              <div className="acc-field-h">
                <label className="acc-label">Sort</label>
                <div className="acc-fh-control">
                  <input className="acc-input" type="number" value={sort} placeholder="(auto)" onChange={(e) => setSort(e.target.value)} />
                </div>
              </div>
              {/* Employees tab (legacy Department detail; Child Department tab removed). */}
              <div className="acc-tabs">
                <button type="button" className="acc-tab active"><i className="ti ti-users" /> Employees</button>
              </div>
              <DepartmentEmployeesPanel departmentId={editing?.id} staged={stagedEmpIds} onStagedChange={setStagedEmpIds} />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void submit('save')} disabled={save.isPending}><span className="tb-i">💾</span> {save.isPending ? 'Saving…' : 'Save'}</button>
              <button className="ca-btn" onClick={() => void submit('close')} disabled={save.isPending}><span className="tb-i">💾</span> Save and Close</button>
              <button className="ca-btn" onClick={() => void submit('new')} disabled={save.isPending}><span className="tb-i">💾</span> Save and New</button>
              <button className="ca-btn" onClick={close}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
